import { generatePath, useParams } from "react-router-dom";

import { useRecoilValue } from "recoil";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { defaultOrganizationContainerSelector } from "../../core/recoil/organizations";
import { AbsoluteRoute, type RouteParams } from "../../core/routes/absoluteRoutes";
import { CofConfigId, ResourceKind } from "../../gql-generated/types";
import { FEATURE_ORG_ROUTE_MAP, Feature, LENS_FOLDER_FEATURE_ROUTE_MAP, NEW_USER_FEATURES } from "../models";

import { useIsInitialized } from "./useIsInitialized";
import { useOrganizationsV2 } from "./useOrganizationsV2";
import { useSelectedLensCentralOrganizationNotIncludesCamkitOrgs } from "./useSelectedLensCentralOrganizationNotIncludesCamkitOrgs";
import { useSelectedLensFolderV3 } from "./useSelectedLensFolderV3";
import { useSelectedOrganization } from "./useSelectedOrganization";
import { useSelectedOrganizationContainer } from "./useSelectedOrganizationContainer";

import { useOrgChildResources, useSelectedLensFolder } from ".";

export const usePageRedirect = (feature?: Feature): string | null => {
    const isInitialized = useIsInitialized();
    const { orgId: paramOrgId, lensFolderId: paramLensFolderId } =
        useParams<RouteParams[AbsoluteRoute.ORG_LENS_FOLDER_INSIGHTS]>();
    const { configs } = useFeatureGates();
    const defaultOrganizationContainerV1 = useRecoilValue(defaultOrganizationContainerSelector);
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const { defaultOrganizationContainer: defaultOrganizationContainerV2 } = useOrganizationsV2();
    const defaultOrganizationContainer = isMyLensesOrgsV2Enabled
        ? defaultOrganizationContainerV2
        : defaultOrganizationContainerV1;

    const { orgId: selectedOrganizationId } = useSelectedOrganizationContainer();
    const selectedLensCentralOrganization = useSelectedLensCentralOrganizationNotIncludesCamkitOrgs();

    const selectedOrganizationV2 = useSelectedOrganization();
    const selectedOrganization = isMyLensesOrgsV2Enabled ? selectedOrganizationV2 : selectedLensCentralOrganization;

    const selectedLensFolderV1 = useSelectedLensFolderV3();

    const { lensFolder: selectedLensFolderV2 } = useSelectedLensFolder();

    const selectedLensFolder = isMyLensesOrgsV2Enabled ? selectedLensFolderV2 : selectedLensFolderV1;

    const { resourceContainers, loading: isLensFoldersLoading } = useOrgChildResources({
        kind: ResourceKind.LensSpaces,
    });

    if (!isInitialized) {
        return null;
    }

    if (!defaultOrganizationContainer) {
        const welcomeRoute = `${AbsoluteRoute.WELCOME}/${paramOrgId ?? ""}`;

        return feature && NEW_USER_FEATURES.includes(feature) ? null : welcomeRoute;
    }

    if (!selectedOrganizationId) {
        const orgRoute = feature ? FEATURE_ORG_ROUTE_MAP[feature] : undefined;
        if (orgRoute) {
            return generatePath(orgRoute, { orgId: defaultOrganizationContainer.id });
        }
    } else if (!!selectedOrganization && !selectedLensFolder?.id && !paramLensFolderId) {
        // only Lens Central features that need lens folder id
        const orgId = selectedOrganization.id;
        const lensFolderV1 = selectedLensCentralOrganization?.lensSpacesList[0];

        if (isLensFoldersLoading) {
            return null;
        }
        const lensFolder = isMyLensesOrgsV2Enabled ? resourceContainers[0] : lensFolderV1;

        if (lensFolder) {
            if (feature === Feature.lenses) {
                return generatePath(AbsoluteRoute.ORG_LENS_FOLDER_LENSES, {
                    orgId,
                    lensFolderId: lensFolder.id,
                });
            }
            const isDefaultOrg = selectedOrganization.id === defaultOrganizationContainer.id;
            const lensFolderFeatureRoute =
                feature && !isDefaultOrg ? LENS_FOLDER_FEATURE_ROUTE_MAP[feature] : undefined;
            if (lensFolderFeatureRoute) {
                return generatePath(lensFolderFeatureRoute, {
                    orgId,
                    lensFolderId: lensFolder.id,
                });
            }
        } else {
            if (configs[CofConfigId.MyLensesUxImprovements].enabled) {
                if (feature === Feature.lenses) {
                    return generatePath(AbsoluteRoute.LENS_FOLDERS);
                }
            } else {
                // this will not be default organization, it is some other org with no folders
                // we redirect back to the feature, where default org + folder will be selected
                if (feature === Feature.lenses) {
                    return generatePath(AbsoluteRoute.LENSES);
                }
                if (feature === Feature.insights) {
                    return generatePath(AbsoluteRoute.INSIGHTS);
                }
                if (feature === Feature.monetization) {
                    return generatePath(AbsoluteRoute.MONETIZATION);
                }
            }
        }
    }

    return null;
};
